/* eslint-disable */
import React from 'react';
import {
  Autocomplete, Chip, CircularProgress, FormControl, InputLabel, TextField,
  ListSubheader,
} from '@mui/material';
import { VariableSizeList } from 'react-window';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { CommonElementProps } from '../types';
import ElementUtils from '../utils';

export type AutoCompleteOption<T, V> = {
  label: T;
  subLabel?: T;
  value: V;
};

export interface AutoCompleteProps<T, V> extends CommonElementProps<V[]> {
  options: AutoCompleteOption<T, V>[];
  onChange?: (value: V) => void;
  onSelectClose?: () => void;
  loading?: boolean;
  subLabel?: string;
}

interface ListboxComponentProps extends React.HTMLAttributes<HTMLElement> {
  hasSubLabel?: boolean; // Add hasSubLabel to the props
}

const LISTBOX_PADDING = 8;

function renderRow(props: any) {
  const { data, index, style } = props;
  const dataSet = data[index];

  return (
    <div style={style} className="hover:bg-[#f0f1f1]">
      {dataSet}
    </div>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>(
  function ListboxComponent(props, ref) {
    const { children, hasSubLabel, ...other } = props;
    const itemData: any[] = [];
    const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    (children as any[]).forEach((item) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    });

    const itemCount = itemData.length;
    const itemSize = hasSubLabel ? 52 : 38;
    const maxVisibleItems = 8;
    const visibleRows = Math.max(1, Math.min(itemCount, maxVisibleItems));
    const height = visibleRows * itemSize + 2 * LISTBOX_PADDING;

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={height}
            width="100%"
            outerElementType={OuterElementType}
            itemCount={itemCount}
            itemSize={() => itemSize}
            overscanCount={5}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

const AutoComplete = function AutoComplete<T, V>(
  props: AutoCompleteProps<T, V>,
) {
  const {
    loading,
    options,
    onChange,
    placeholder,
    error,
    label,
    disabled,
    onSelectClose,
    value,
  } = props;

  const selectedValue = value as unknown as V | null | undefined;
  const hasSelectedValue = selectedValue != null && selectedValue !== ('' as unknown as V);
  const matchedOption = hasSelectedValue
    ? options.find((x) => x.value === selectedValue || (x.label as unknown) === selectedValue)
    : undefined;
  const defaultValue: AutoCompleteOption<T, V> | null = matchedOption
    ?? (hasSelectedValue
      ? { label: String(selectedValue) as unknown as T, value: selectedValue as V }
      : null);

  return (
    <FormControl className="w-full custom-shadow">
      <InputLabel
        error={!!error}
        color="secondary"
        shrink
        className="text-[1.4rem] font-bold -ml-3 -mt-[18px] text-[#28364C] focus:text-[#28364C]"
      >
        {label}
      </InputLabel>
      <Autocomplete
        value={defaultValue}
        options={options as any}
        ListboxComponent={(listboxProps) => (
          <ListboxComponent {...listboxProps} hasSubLabel={options.some((opt)=>opt?.subLabel)} /> // Pass hasSubLabel here
        )}
        filterOptions={(opts, state) => {
          const inputValue = state.inputValue.toLowerCase().trim();
          if (!inputValue) return opts;
          const result = opts.reduce<
          Array<{ opt: AutoCompleteOption<T, V>; startIndex: number; matchScore: number }>
          >(
            (acc, opt) => {
              const labelCombo = `${opt.label} ${opt.subLabel || ''}`.toLowerCase();
              const startIndex = labelCombo.indexOf(inputValue);
              if (startIndex === -1) return acc;
              let matchScore = 0;
              while (
                matchScore < inputValue.length
                && labelCombo[startIndex + matchScore] === inputValue[matchScore]
              ) {
                matchScore += 1;
              }
              acc.push({ opt, startIndex, matchScore });
              return acc;
            },
            [],
          ).sort((a, b) => (
            (a.startIndex === 0 ? -1 : (b.startIndex === 0 ? 1 : 0))
            || a.startIndex - b.startIndex
            || b.matchScore - a.matchScore
            || (a.opt.label as string).length - (b.opt.label as string).length
          ));
          return result.map((item) => item.opt);
        }}
        getOptionLabel={(option: any) => option.label}
        sx={{
          '& fieldset': { border: 'none' },
        }}
        disabled={disabled}
        loading={loading}
        fullWidth
        renderTags={(selectedValue: any, getTagProps) => (
          selectedValue.map((option: any, index: number) => (
            <Chip variant="outlined" label={option.label} {...getTagProps({ index })} />
          ))
        )}
        isOptionEqualToValue={(option: any, newValue: any) => option?.value === newValue?.value}
        onClose={() => onSelectClose && onSelectClose()}
        onChange={(e, v: any) => {
          if (onChange) onChange(v?.value);
        }}
        renderOption={(optionProps, option, { inputValue }) => {
          const matchesLabel = match(option.label as string, inputValue, { insideWords: true });
          const partsLabel = parse(option.label as string, matchesLabel);
          const subLabel = option.subLabel ? option.subLabel : '';
          const matchesSubLabel = subLabel
            ? match(subLabel as string, inputValue, { insideWords: true }) : [];
          const partsSubLabel = parse(subLabel as string, matchesSubLabel);

          return (
            <li {...optionProps} key={(option.value as string)}>
              <div title={option.label as string} className="truncate">
                {partsLabel.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                    className="text-xs"
                  >
                    {part.text}
                  </span>
                ))}
                {subLabel && (
                <div className="text-xs text-gray-500">
                  {partsSubLabel.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                      className="text-xs"
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
                )}
              </div>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              error: !!error,
              sx: {
                '& fieldset': { border: 'none' },
              },
              classes: {
                root: disabled ? 'pl-0 text-[#28364C] rounded-md' : 'bg-white custom-shadow pl-0 rounded-md',
                error: 'outline outline-[1px] outline-error',
              },
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={14} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && (
        <div className="absolute -bottom-5 text-error text-xs">
          {ElementUtils.renderError(error)}
        </div>
      )}
    </FormControl>
  );
};

AutoComplete.defaultProps = {
  onChange: () => { },
  onSelectClose: () => { },
  loading: false,
};

AutoComplete.propTypes = {

};
export default AutoComplete;
